import {
  CloseCircleOutlined, DoubleRightOutlined, EllipsisOutlined, FilterOutlined,
  PlusOutlined, SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import {
  App,
  Avatar, Button, DatePicker,
  Divider,
  Dropdown, Flex, Input, Modal,
  Popover,
  Select, Space,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import Checkbox from 'components/Checkbox';
import DebouncedInput from 'components/DebouncedInput';
import FileIcon from 'components/FileIcon';
import { pluralize } from 'components/ItemsInput/AddOrderItemOverlay/utils';
import JumpLink from 'components/JumpLink';
import Layout from 'components/Layout';
import LocationSelectInput from 'components/LocationSelectInput';
import SourceInput from 'components/SourceInput';
import Table from 'components/Table';
import UserSelectInput from 'components/UserSelectInput';
import withGuard from 'components/withGuard';
import { WL_CONFIG } from 'constants';
import { AdminContext } from 'constants/contexts';
import { PERMISSIONS } from 'constants/permissions';
import dayjs from 'dayjs';
import { useQueryParams } from 'hooks/useQueryParams';
import React, {
  useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router';
import { useLocation } from 'react-router-dom';
import { formatAmount, priceTransformer } from 'utils';
import { captureException } from 'utils/errors';
import http, { getHttpErrorMessage } from 'utils/http';
import CreateForm from './Create';
import Filter from './Filter';
import ImportCsvDrawer from './ImportCsvDrawer';
import RunSequenceDrawer from './RunSequenceDrawer';
import { SIC_CATEGORY_OPTIONS } from './constants';
import {
  COMPANY_STATUS_OPTIONS, CompanyList, getStatusTagColor, serializeFilters,
} from './utils';

const WORKFLOW_STATUS_OPTIONS = [
  { value: 'Enrolled', label: 'Enrolled' },
  { value: 'Not Enrolled', label: 'Not Enrolled' },
];

const CLIENT_TYPE_OPTIONS = [
  { value: 'corporate', label: 'Corporate' },
  { value: 'individual', label: 'Individual' },
];

function Companies() {
  const navigate = useNavigate();
  const [version, setVersion] = useState(1);
  const [selectedCompany, setSelectedCompany] = useState();
  const { pathname } = useLocation();
  const [formIsOpen, setFormIsOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [sequenceRunnerOpen, setSequenceRunnerOpen] = useState(false);
  const [importCsvOpen, setImportCsvOpen] = useState(false);
  const latestFilters = useRef({});

  const [filters, setFilters] = useQueryParams({
    status: undefined,
    sicCategory: undefined,
    owner: undefined,
    market: undefined,
    client_type: CLIENT_TYPE_OPTIONS[0].value,
  });

  const { message } = App.useApp();
  const resetPageRef = useRef(false);
  const { organizationId } = useContext(AdminContext);

  // Selection state
  const [selectionVersion, setSelectionVersion] = useState(0);
  const selectedCount = CompanyList.getSelectedCount();
  const isSelectAllMode = CompanyList.getSelection().selectAll;

  useEffect(() => {
    setVersion((v) => v + 1);
  }, [pathname, organizationId]);

  const navigateToDetail = ({ id, index }) => {
    navigate(`/console/companies/${id}/info`);
  };

  // Selection handlers
  const handleSelectAll = (selected) => {
    CompanyList.setSelectAll(selected);
    setSelectionVersion((v) => v + 1);
  };

  const handleRowSelect = (record, selected) => {
    CompanyList.toggleSelection(record.id);
    setSelectionVersion((v) => v + 1);
  };

  const handleSelectMultiple = (selected, selectedRows, changeRows) => {
    changeRows.forEach((row) => {
      CompanyList.toggleSelection(row.id);
    });
    setSelectionVersion((v) => v + 1);
  };

  const clearSelection = () => {
    CompanyList.clearSelection();
    setSelectionVersion((v) => v + 1);
  };

  const columns = useMemo(() => [
    ...DEFAULT_COLUMNS,
    {
      key: 'action',
      align: 'right',
      width: 50,
      onCell: (record) => ({
        onClick: (e) => {
          e.stopPropagation();
        },
      }),
      render(_, data, index) {
        return (
          <Dropdown menu={{
            items: [
              {
                key: '1',
                label: <a type="text" onClick={(e) => navigateToDetail({ id: data.id, index })}>Edit</a>,
              },
              {
                key: '2',
                label: <a type="text" onClick={(e) => setSelectedCompany(data)}>Delete</a>,
              },
            ],
          }}
          >
            <EllipsisOutlined />
          </Dropdown>
        );
      },
    },

  ], [navigate]);

  // Row selection configuration
  const rowSelection = useMemo(() => ({
    type: 'checkbox',
    selectedRowKeys: CompanyList.getPageSelectedIds(companies),
    onSelect: handleRowSelect,
    getCheckboxProps: (record) => ({
      name: record.name,
    }),
    renderCell: (checked, record) => (
      <Checkbox
        checked={checked}
        onClick={(e) => {
          e.stopPropagation();
          handleRowSelect(record);
        }}
      />
    ),
    // Custom logic to handle the "select all" checkbox state across pages
    onSelectAll: (selected, selectedRows, changeRows) => {
      if (selected) {
        // User clicked "select all" and all visible rows would be selected
        // This means they want to select all companies across all pages
        handleSelectAll(true);
      } else if (!selected && isSelectAllMode) {
        // User clicked "unselect all" while in select-all mode
        // This means they want to clear the selection
        clearSelection();
      } else {
        // Regular row selection change
        handleSelectMultiple(selected, selectedRows, changeRows);
      }
    },
  }), [companies, selectionVersion, isSelectAllMode]);

  function handleFilterChange(payload) {
    resetPageRef.current = true;
    setFilters(payload);
    setLoading(true);
  }

  const fetcher = useCallback(
    (pagination, filter, sorter) => {
      latestFilters.current = serializeFilters(filters);
      const queries = {
        page: filters.search || resetPageRef.current ? 1 : pagination.current,
        page_size: pagination.pageSize,
        ordering: '-status_order,-last_activity',
        ...latestFilters.current,
      };
      if (sorter?.order && sorter?.columnKey) {
        queries.ordering = `${sorter?.order === 'descend' ? '-' : ''}${sorter?.columnKey},-last_activity,id`;
      } else {
        queries.ordering = '-status_order,-last_activity,id';
      }

      resetPageRef.current = false;
      setLoading(true);
      return CompanyList.fetch(queries)
        .then(({ data, total, page }) => {
          setCompanies(data);
          return {
            page,
            data,
            total,
          };
        }).finally(() => setLoading(false));
    },
    [filters, version],
  );

  const onDeleteConfirm = () => {
    setDeleteLoading(true);
    return http.delete(`v1/organizations/{{organizationId}}/companies/${selectedCompany.id}/`)
      .then(() => {
        setSelectedCompany();
        setVersion((v) => v + 1);
      })
      .catch((e) => {
        message.error(getHttpErrorMessage(e));
        captureException(e);
      })
      .finally(() => setDeleteLoading(false));
  };

  const onClose = (needRefresh) => {
    setFormIsOpen(false);
    if (needRefresh) {
      setVersion((v) => v + 1);
    }
  };

  const hasActiveFiltersCount = Object.entries(filters).filter(([key, value]) => value != null && !['search'].includes(key)).length;
  return (
    <Layout header={(
      <Layout.Header
        title={(
          <Flex align="center" justify="space-between" className="sdf">
            <div>
              Companies
              {' '}
              <small style={{ color: 'rgba(0, 0, 0, 0.45)' }}>
                (
                {CompanyList.count}
                )
              </small>
            </div>

          </Flex>
        )}
        extra={(
          <>
            <Tooltip
              placement="bottomRight"
              title={selectedCount === 0 ? 'please make a selection to run sequences' : ''}
            >
              <Button
                size="large"
                disabled={selectedCount === 0}
                onClick={() => setSequenceRunnerOpen(true)}
              >
                Run Sequences
                {selectedCount > 0 && ` (${selectedCount})`}
              </Button>
            </Tooltip>
            <Divider type="vertical" className="h-full" />
            <Button
              size="large"
              onClick={() => setImportCsvOpen(true)}
              icon={<UploadOutlined />}
            >
              Import
            </Button>
            <Button
              key="new-company-action"
              type="primary"
              size="large"
              icon={<PlusOutlined />}
              onClick={() => setFormIsOpen(true)}
            >
              Add Company
            </Button>

          </>
        )}
      />
    )}
    >
      {/* {!filters.search && !companies.length && !loading && hasActiveFiltersCount === 0 && (
        <Space direction="vertical" className="text-center justify-center w-full h-[90vh]">
          {loading && <Spin size="large" />}
          {!loading && (
            <Space direction="vertical" className="justify-center align-items-center w-full h-[90vh]">
              <FileIcon />
              <Typography.Paragraph>No companies yet—add one now to manage your leads!</Typography.Paragraph>
              <Button
                onClick={() => setFormIsOpen(true)}
                type="primary"
                icon={<PlusOutlined />}
              >
                Add your first company
              </Button>
            </Space>
          )}
        </Space>
      )} */}
      <div>

        <div className="flex flex-col gap-3">
          <div className="flex justify-between">
            <DebouncedInput
              placeholder="Company name"
              value={filters.search || ''}
              suffix={filters.search ? <CloseCircleOutlined onClick={() => handleFilterChange({ search: '' })} /> : <SearchOutlined style={{ color: 'rgba(0, 0, 0, 0.45)' }} />}
              onChange={(value) => handleFilterChange({ search: value })}
              style={{ width: 422 }}
              size="large"
            />
            <Button
              color={hasActiveFiltersCount > 0 ? 'primary' : 'default'}
              variant="outlined"
              size="large"
              icon={<FilterOutlined />}
              onClick={() => setShowFilters(!showFilters)}
            >
              {
                hasActiveFiltersCount > 0 ? `${hasActiveFiltersCount} ${pluralize('filter', hasActiveFiltersCount)} selected` : 'Filter'
              }
            </Button>

          </div>

          <div className="gap-3 flex-wrap items-start" style={{ display: showFilters ? 'flex' : 'none' }}>
            <Filter
              name="Client type"
              value={filters.client_type}
              onChange={(client_type) => handleFilterChange({ client_type })}
            >
              <Select
                placeholder="Client type"
                allowClear
                style={{ width: 150 }}
                options={CLIENT_TYPE_OPTIONS}
                labelInValue={false}
                // value={filters.client_type}
                // onChange={(client_type) => handleFilterChange({ client_type })}
              />
            </Filter>
            <Filter name="Status" value={filters.status} onChange={(status) => handleFilterChange({ status })}>
              <Select
                placeholder="Status"
                allowClear
                style={{ width: 150 }}
                options={COMPANY_STATUS_OPTIONS}
              />
            </Filter>
            <Filter name="SIC category" value={filters.sicCategory} onChange={(sicCategory) => handleFilterChange({ sicCategory })}>
              <Select
                placeholder="SIC category"
                allowClear
                style={{ width: 150 }}
                options={SIC_CATEGORY_OPTIONS}
              />
            </Filter>
            <Filter
              name="Company owner"
              value={filters.owner}
              onChange={(owner) => handleFilterChange({ owner })}
            >
              <UserSelectInput
                placeholder="Company owner"
                style={{ width: 150 }}
              />
            </Filter>
            <Filter
              name="Date created"
              value={filters.created_date}
              onChange={(created_date) => handleFilterChange({ created_date })}
              renderLabel={(value) => value && dayjs(value).format('MMM DD, YYYY')}
            >
              <DatePicker placeholder="Date created" className="w-[150px]" />
            </Filter>
            <Filter
              name="Market"
              value={filters.market}
              onChange={(market) => handleFilterChange({ market })}
            >
              <LocationSelectInput
                placeholder="Market"
                mode={undefined}
                labelInValue
                style={{ width: 150 }}
              />
            </Filter>
            <Filter
              name="Workflow status"
              value={filters.workflow_status}
              onChange={(workflow_status) => handleFilterChange({ workflow_status })}
            >
              <Select
                placeholder="Workflow status"
                allowClear
                style={{ width: 150 }}
                options={WORKFLOW_STATUS_OPTIONS}
              />
            </Filter>
            <Filter
              name="Origin"
              value={filters.source}
              onChange={(source) => handleFilterChange({ source })}
            >
              <SourceInput placeholder="Origin" style={{ width: 150 }} />
            </Filter>

          </div>
        </div>

        <div
          className="flex items-center mt-2"
          style={{ visibility: selectedCount > 0 ? 'visible' : 'hidden' }}
        >
          <Typography.Text className="m-2">
            {
              isSelectAllMode ? (
                <>
                  <strong>
                    All
                    {' '}
                    {CompanyList.count}
                    {' '}
                    companies
                  </strong>
                  {' '}
                  are selected
                  {CompanyList.getSelection().excludedIds.length > 0 && (
                    <>
                      {' '}
                      (except
                      {' '}
                      {CompanyList.getSelection().excludedIds.length}
                      {' '}
                      deselected)
                    </>
                  )}
                </>
              ) : (
                <>
                  <strong>{selectedCount}</strong>
                  {' '}
                  {pluralize('company', selectedCount)}
                  {' '}
                  selected
                </>
              )
            }
          </Typography.Text>
          <Button
            size="small"
            type="link"
            onClick={clearSelection}
          >
            Clear selection
          </Button>
        </div>

        <Table
          id="companies"
          fetchFn={fetcher}
          columns={columns}
          pagination={{ pageSize: 50 }}
          scroll={{ x: 2200 }}
          rowSelection={rowSelection}
          empty={(
            <Space direction="vertical" className="my-10 mx-5">
              <FileIcon />
              <Typography.Paragraph>No companies match your search criteria!</Typography.Paragraph>
            </Space>
          )}
          onRow={(record, index) => ({
            onClick: (e) => {
              // console.log(e);
              // return;
              if (e.isLink) {
                return;
              }

              // Check if click is on selection cell (handled by custom renderCell now)
              const { target } = e;
              const isSelectionCell = target.classList.contains('ant-table-selection-column');

              if (isSelectionCell) {
                // Selection is handled by the custom renderCell function
                return;
              }

              navigateToDetail({ id: record.id, index });
            },
          })}
        />

      </div>
      <CreateForm open={formIsOpen} onClose={onClose} />

      <Modal
        title={`Delete ${selectedCompany?.name}`}
        open={!!selectedCompany}
        onCancel={() => setSelectedCompany()}
        onOk={onDeleteConfirm}
        okButtonProps={{ danger: true, loading: deleteLoading }}
        okText="Delete"
        cancelText="Cancel"
      >
        <Typography.Text>
          By confirming, all company information will be permanently deleted.
        </Typography.Text>
      </Modal>
      <RunSequenceDrawer
        open={sequenceRunnerOpen}
        onClose={() => setSequenceRunnerOpen(false)}
        selections={CompanyList.getSelectedIds()}
        companyFilters={latestFilters.current}
        companiesCount={selectedCount > 0 ? selectedCount : CompanyList.count}
        onFinish={clearSelection}
      />
      <ImportCsvDrawer
        open={importCsvOpen}
        onClose={() => setImportCsvOpen(false)}
        onSuccess={() => {
          setVersion((v) => v + 1);
        }}
      />
    </Layout>
  );
}

const DEFAULT_COLUMNS = [
  {
    title: 'Name',
    key: 'name',
    width: 200,
    ellipsis: false,
    render: (_, record) => (
      <Flex gap={4} align="center">
        <JumpLink href={`/console/companies/${record.id}/info`} text={record.name} />
        {record.active_workflows?.length > 0 && (
          <Popover
            styles={{
              root: {
                maxWidth: 300,
              },
            }}
            content={(
              <div>
                {`This company is enrolled in the "${record.active_workflows.join('" and "')}" sequence${record.active_workflows.length > 1 ? 's' : ''}.`}
              </div>
            )}
            title=""
          >
            <DoubleRightOutlined
              style={{
                color: WL_CONFIG.colors.primary,
                border: `1px solid ${WL_CONFIG.colors.primary}`,
                borderRadius: 2,
                fontSize: 10.5,
                padding: 1,
                cursor: 'pointer',
              }}
            />
          </Popover>
        )}

      </Flex>
    ),
  },
  {
    title: 'Status',
    key: 'status_order',
    width: 100,
    ellipsis: false,
    align: 'center',
    render: (_, record) => (
      <Tag color={getStatusTagColor(record.status)}>
        {record.status}
      </Tag>
    ),
  },
  {
    title: 'SIC Category',
    key: 'sic_category',
    width: 150,
    ellipsis: false,
  },
  {
    title: 'Origin',
    key: 'source',
    width: 250,
    ellipsis: false,
  },
  {
    title: 'Owner',
    key: 'owner',
    width: 250,
    ellipsis: false,
    render: (owner) => {
      if (!owner) {
        return <Typography.Text type="secondary">-</Typography.Text>;
      }

      const displayName = owner.first_name && owner.last_name
        ? `${owner.first_name} ${owner.last_name}`
        : owner.email;

      return (
        <Flex gap={4} align="center" className="w-full overflow-hidden">
          <Avatar
            size="small"
            src={owner.avatar}
            style={{
              backgroundColor: owner.avatar ? 'transparent' : '#FFE4D6',
              color: '#D2691E',
              fontWeight: 600,
              fontSize: '11px',
            }}
          >
            {!owner.avatar && (owner.first_name?.[0] || owner.email?.[0] || '?').toUpperCase()}
          </Avatar>
          <Typography.Text style={{
            whiteSpace: 'nowrap',
            color: '#333',
            fontWeight: 500,
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            width: 'calc(100% - 15px)',
          }}
          >
            {displayName}
          </Typography.Text>
        </Flex>
      );
    },
  },
  {
    title: 'Market',
    key: 'market',
    width: 150,
    ellipsis: false,
    render: (value) => value?.title,
  },
  {
    title: 'Order count',
    key: 'orders_count',
    width: 150,
    ellipsis: false,
    sorter: true,
  },
  {
    title: 'total order value',
    key: 'orders_total',
    width: 200,
    ellipsis: false,
    render: (value) => formatAmount(value),
  },
  {
    title: 'last order',
    key: 'last_order',
    width: 150,
    ellipsis: false,
    render: (value) => value && dayjs(value).format('MMM DD, hh:mm A'),
  },
  {
    title: 'order frequency',
    key: 'order_frequency',
    width: 150,
    ellipsis: false,
    render: (value) => value || '-',
  },
  {
    title: 'last sequence run',
    key: 'last_workflow',
    width: 150,
    ellipsis: false,
    render: (value) => value && dayjs(value).format('MMM DD, hh:mm A'),
  },
  {
    title: 'last activity',
    key: 'last_activity',
    width: 150,
    ellipsis: false,
    render: (value) => value && dayjs(value).format('MMM DD, hh:mm A'),
  },
];

export default withGuard(Companies, [PERMISSIONS.COMPANIES], { redirect: true });
